import noop from 'lodash-es/noop';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import variables from '../../variables';

const colorSchemes = {
  transparent: {
    color: 'var(--primaryColor)',
    hoverColor: variables.colors.highlightBlue,
    backgroundColor: variables.colors.transparent,
    hoverBackgroundColor: variables.colors.transparent,
    borderColor: 'var(--primaryColor)',
    hoverBorderColor: variables.colors.highlightBlue,
  },
  blue: {
    color: variables.colors.white,
    hoverColor: variables.colors.white,
    backgroundColor: 'var(--primaryColor)',
    hoverBackgroundColor: variables.colors.highlightBlue,
    borderColor: 'var(--primaryColor)',
    hoverBorderColor: variables.colors.highlightBlue,
  },
  white: {
    color: variables.colors.white,
    hoverColor: variables.colors.lightestGrey,
    backgroundColor: variables.colors.transparent,
    hoverBackgroundColor: variables.colors.transparent,
    borderColor: variables.colors.white,
    hoverBorderColor: variables.colors.lightestGrey,
  },
  red: {
    color: variables.colors.red,
    hoverColor: variables.colors.warningRed,
    backgroundColor: variables.colors.transparent,
    hoverBackgroundColor: variables.colors.transparent,
    borderColor: variables.colors.red,
    hoverBorderColor: variables.colors.warningRed,
  },
  black: {
    color: variables.colors.black,
    hoverColor: variables.colors.darkGrey,
    backgroundColor: variables.colors.transparent,
    hoverBackgroundColor: variables.colors.transparent,
    borderColor: variables.colors.black,
    hoverBorderColor: variables.colors.darkGrey,
  },
};

/**
 * Dialog button component
 *
 * <Button color="transparent">Click me</Button>
 */
const Button = styled.button`
  display: block;
  height: 33px;
  margin: 0;
  padding: 0 10px;
  font-size: 15px;
  font-family: Roboto, sans-serif;
  font-weight: 600;
  line-height: 33px;
  color: ${props => colorSchemes[props.color].color};
  background-color: ${props => colorSchemes[props.color].backgroundColor};
  border: thin solid ${props => colorSchemes[props.color].borderColor};
  border-radius: 5px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  outline: none;

  &:hover {
    color: ${props => colorSchemes[props.color].hoverColor};
    background-color: ${props =>
      colorSchemes[props.color].hoverBackgroundColor};
    border-color: ${props => colorSchemes[props.color].hoverBorderColor};
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &:focus {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
  }

  & del {
    opacity: 0.5;
  }
`;
Button.propTypes = {
  color: PropTypes.oneOf(['transparent', 'blue', 'white', 'red', 'black']),
  type: PropTypes.string,
  onClick: PropTypes.func,
};
Button.defaultProps = {
  color: 'transparent',
  type: 'button',
  onClick: noop,
};
Button.displayName = 'Button';
export default Button;

export const ButtonLink = styled(Button.withComponent('a'))`
  text-decoration: none;
  display: inline-block;
`;
ButtonLink.propTypes = {
  color: PropTypes.oneOf(['transparent', 'blue', 'white', 'red']),
  type: PropTypes.string,
  onClick: PropTypes.func,
};
ButtonLink.defaultProps = {
  color: 'transparent',
  type: 'button',
  onClick: noop,
};

/**
 * Big button for upgrade dialogs
 *
 * <BigButon>
 *   <strong ... />
 *   <small ... />
 * </BigButton>
 */
export const BigButton = styled(Button)`
  width: 100%;
  height: auto;
  line-height: normal;
  padding: 12px 20px;
  & > strong {
    display: block;
    font-size: 18px;
    line-height: 1.2em;
  }
  & > small {
    display: block;
    margin-top: 3px;
    font-size: 12px;
    line-height: 1.2em;
    font-weight: normal;
  }
`;

/**
 * Dialog button group
 *
 * <ButtonGroup>
 *   <Button ... />
 *   <Button ... />
 * </ButtonGroup>
 */
export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${Button} {
    flex: 1;

    &:last-child {
      flex: 4;
    }

    &:not(:last-child) {
      margin-right: 15px;
    }
  }
`;
ButtonGroup.displayName = 'ButtonGroup';
